import { useMemo, useState } from "react";
import MenuFrame from "../frames/menu_frame";
import { Box, Button, Chip, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, } from "@mui/material";
import { Add, ArrowForwardIos, CheckCircle, EmailOutlined, LocalPhoneOutlined, PaymentsOutlined, SearchOff, WarningAmber, } from "@mui/icons-material";
import { green, orange, grey } from "@mui/material/colors";
import { getAllVendor } from "./vendor_halper";
import VendorDetails from "./vendor_details";
import Loading1 from "../frames/loading1";
import { useNavigate } from "react-router-dom";
import HtmlTooltip from "../frames/html_tooltip";
import { useLanguage } from "../LanguageContext";

const getAddressLines = (...values) => values.filter((value) => value !== null && value !== undefined && String(value).trim() !== "");

const MissingValue = ({ label = "Missing" }) => (
    <Chip
        size="small"
        icon={<WarningAmber sx={{ fontSize: 15 }} />}
        label={label}
        sx={{
            height: 24,
            fontSize: 12,
            color: orange[800],
            backgroundColor: orange[50],
            border: `1px solid ${orange[100]}`,
            "& .MuiChip-icon": { color: orange[600] },
        }}
    />
);

const StatusChip = ({ status }) => {
    const isActive = status === "Active";
    return (
        <Chip
            size="small"
            icon={isActive ? (<CheckCircle sx={{ fontSize: 16 }} />) : undefined}
            label={status || "Unknown"}
            sx={{
                height: 26,
                fontWeight: 600,
                fontSize: 12,
                color: isActive ? green[700] : grey[700],
                backgroundColor: isActive ? green[50] : grey[100],
                border: `1px solid ${isActive ? green[100] : grey[300]}`,
                "& .MuiChip-icon": { color: green[600] },
            }}
        />
    );
};

function VendorList() {
    const [vendorList, setVendorList] = useState(null);
    const [allVendors, setAllVendors] = useState([]);
    const [activeVendor, setActiveVendor] = useState(null);
    const [popup, setPopup] = useState(null);
    const { text } = useLanguage();
    const navigate = useNavigate();

    const initData = async (auth) => {
        if (!auth) return;
        try {
            const data = await getAllVendor();
            const safeData = Array.isArray(data) ? data : [];
            setAllVendors(safeData);
            setVendorList(safeData);
        } catch (err) {
            console.error("Failed to load vendors:", err);
            setAllVendors([]);
            setVendorList([]);
        }
    };

    const handleClose = () => {
        setActiveVendor(null);
        setPopup(null);
    };

    const searchVendor = (e) => {
        const searchValue = e.target.value.trim().toLowerCase();
        if (!searchValue) {
            setVendorList(allVendors);
            return;
        }
        const filtered = allVendors.filter((vendor) => {
            const searchableText = [vendor.name, vendor.vendor_id, vendor.gst_num, vendor.phone, vendor.email].filter(Boolean).join(" ").toLowerCase();
            return searchableText.includes(searchValue);
        });
        setVendorList(filtered);
    };

    const openVendor = (vendor) => {
        setActiveVendor(vendor);
        setPopup("vendor");
    };

    const activeCount = useMemo(() => allVendors.filter((vendor) => vendor.status === "Active").length,
        [allVendors]
    );

    return (
        <MenuFrame permission="VENDOR" onInit={initData} screen="VENDOR_LIST" onSearch={searchVendor}>
            <VendorDetails open={popup === "vendor"} data={activeVendor} handleClose={handleClose} />

            <Box sx={{ mt: 1 }}>
                <Stack sx={{ display: { xs: "block", sm: "block", md: "flex" }, flexDirection: "row", justifyContent: "space-between", alignItems: "center", }}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>{text("Vendors")}</Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <Typography variant="body2" color="textSecondary">{allVendors.length} {text("vendors")}</Typography>
                            <Typography variant="body2" color="textDisabled">•</Typography>
                            <Typography variant="body2" color="success" sx={{ fontWeight: 600 }}>{activeCount} {text("active")}</Typography>
                        </Stack>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} >
                        <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/vendor/new")} sx={{ textTransform: "none", borderRadius: 2 }}> {text("New Vendor")} </Button>
                        <Button variant="outlined" startIcon={<PaymentsOutlined />} onClick={() => navigate("/vendor/pay")} sx={{ textTransform: "none", borderRadius: 2 }}> {text("Vendor Payment")} </Button>
                        <Button variant="outlined" onClick={() => navigate("/vendor/payments")} sx={{ textTransform: "none", borderRadius: 2 }}> {text("Payment History")} </Button>
                    </Stack>
                </Stack>

                <Paper sx={{ mt: 2 }}>
                    {vendorList === null ? (
                        <Box sx={{ py: 8 }}><Loading1 /></Box>
                    ) : vendorList.length === 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "row", p: 2 }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: grey[100], mb: 2 }}>
                                <SearchOff color="disabled" />
                            </Box>
                            <Box sx={{ pl: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>{text("No vendor found")}</Typography>
                                <Typography variant="body2" color="textSecondary">{text("Try changing your search or add a new vendor.")}</Typography>
                            </Box>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table aria-label={text("vendor_list")} sx={{ minWidth: 900, "& .MuiTableCell-root": { borderColor: "divider" } }}>
                                <TableHead sx={{ backgroundColor: grey[50] }}>
                                    <TableRow sx={{ '&:last-child td, &:last-child th': { fontWeight: "bold" } }}>
                                        <TableCell>{text("vendor_id")}</TableCell>
                                        <TableCell>{text("name")}</TableCell>
                                        <TableCell align="right">{text("gst")}</TableCell>
                                        <TableCell align="right">{text("phone")}</TableCell>
                                        <TableCell>{text("mail")}</TableCell>
                                        <TableCell>{text("created")}</TableCell>
                                        <TableCell>{text("status")}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {vendorList.map((row) => {
                                        const billingAddress = getAddressLines(row.billing_add_1, row.billing_add_2, row.billing_add_3, [row.billing_add_state, row.billing_add_zip].filter(Boolean).join(" "));
                                        const shippingAddress = getAddressLines(row.shipping_add_1, row.shipping_add_2, row.shipping_add_3, [row.shipping_add_state, row.shipping_add_zip].filter(Boolean).join(" "));
                                        return (
                                            <TableRow
                                                key={row.vendor_id}
                                                hover
                                                tabIndex={0}
                                                onClick={() => openVendor(row)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        openVendor(row);
                                                    }
                                                }}
                                                sx={{
                                                    cursor: "pointer",
                                                    transition: "background-color 0.15s ease",
                                                    "&:hover": { backgroundColor: "action.hover", },
                                                    "&:focus-visible": { outline: "2px solid", outlineColor: "primary", outlineOffset: -2 },
                                                    "&:last-child td": { borderBottom: 0, },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.vendor_id}</Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <HtmlTooltip arrow
                                                        title={
                                                            <Box sx={{ p: 0.5, }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                                                                    {text("Billing Address")}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                                                    {billingAddress.join("\n") || text("No address available")}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    >
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", }}>
                                                                {row.name || text("Unnamed Vendor")}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {text("View details")}
                                                            </Typography>
                                                        </Box>
                                                    </HtmlTooltip>
                                                </TableCell>

                                                <TableCell>
                                                    {row.gst_num ? (
                                                        <HtmlTooltip arrow
                                                            title={
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>{text("Shipping Address")}</Typography>
                                                                    <Typography variant="body2" sx={{ whiteSpace: "pre-line", }}>{shippingAddress.join("\n") || text("No address available")}</Typography>
                                                                </Box>
                                                            }
                                                        >
                                                            <Typography variant="body2" fontWeight={500}>{row.gst_num}</Typography>
                                                        </HtmlTooltip>
                                                    ) : (
                                                        <MissingValue />
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {row.phone ? (
                                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }}>
                                                            <LocalPhoneOutlined sx={{ fontSize: 16, color: "textSecondary", }} />
                                                            <Typography variant="body2">{row.phone}</Typography>
                                                        </Stack>
                                                    ) : (
                                                        <MissingValue />
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {row.email ? (
                                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: "center" }}>
                                                            <EmailOutlined sx={{ fontSize: 17, color: "textSecondary", }} />
                                                            <Typography variant="body2" sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {row.email}
                                                            </Typography>
                                                        </Stack>
                                                    ) : (
                                                        <MissingValue />
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" color="textSecondary" noWrap>
                                                        {row.created || "-"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <StatusChip status={row.status} />
                                                </TableCell>

                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={text("View vendor")}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openVendor(row);
                                                        }}
                                                        sx={{ color: "textSecondary" }}
                                                    >
                                                        <ArrowForwardIos sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {vendorList?.length > 0 && (
                        <>
                            <Divider />
                            <Box sx={{ p: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                    {text("Showing")} {vendorList.length}{" "} {text("vendors")}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {text("Click a row to view details")}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </MenuFrame>
    );
}

export default VendorList;
